import { NewFeatureBadge } from "./new-feature-badge";
import { useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import logoDarkImg from "@assets/Untitled_design_(10)_1773926263699.png";
import { 
  Home, 
  FileText, 
  Users, 
  BookOpen, 
  TrendingUp, 
  Settings, 
  LogOut,
  BrainCircuit,
  Gift,
  Landmark,
  Palette,
  Zap,
  ListChecks,
  PenLine,
  ClipboardList,
  Inbox,
  Wrench,
  Sparkles,
  SearchCode,
  UserX,
  Library,
  Mail,
  ClipboardCheck,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/user-avatar";
import { useAuth } from "@/hooks/use-auth";

export function AppSidebar() {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const { setOpenMobile } = useSidebar();

  // Auto-close sidebar on mobile whenever the user navigates
  useEffect(() => {
    setOpenMobile(false);
  }, [location]);

  const { data: adminCheck } = useQuery<{ isAdmin: boolean }>({
    queryKey: ["/api/admin/check"],
    enabled: !!user,
  });

  const { data: pendingData } = useQuery<{ count: number }>({
    queryKey: ["/api/shares/pending-count"],
    enabled: !!user,
    refetchInterval: 60000,
  });

  const isAdminUser = adminCheck?.isAdmin === true;
  const pendingShareCount = pendingData?.count || 0;

  return (
    <Sidebar>
      <SidebarHeader className="p-4 border-b">
        <Link href="/dashboard" className="flex items-center">
          <img src={logoDarkImg} alt="The Proper Tool" className="h-10 w-auto object-contain" />
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="p-3 space-y-2" data-testid="group-quote-options">
          <Button asChild className="w-full relative" data-testid="button-new-quote">
            <Link href="/quotes/new">
              <Zap className="h-4 w-4 absolute left-3" />
              Auto Quote
            </Link>
          </Button>
          <Button asChild className="w-full relative" data-testid="button-template-quote">
            <Link href="/quotes/template">
              <ListChecks className="h-4 w-4 absolute left-3" />
              Template Quote
            </Link>
          </Button>
          <Button asChild className="w-full relative" data-testid="button-manual-quote">
            <Link href="/quotes/manual">
              <PenLine className="h-4 w-4 absolute left-3" />
              Manual Quote
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full relative bg-sidebar hover:bg-sidebar-accent text-primary border-2 border-primary hover:text-primary font-semibold" data-testid="button-site-survey">
            <Link href="/quotes/survey">
              <ClipboardList className="h-4 w-4 absolute left-3 text-primary" />
              Site Survey
            </Link>
          </Button>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-sidebar-foreground">Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[
                { title: "Dashboard", url: "/dashboard", icon: Home },
                { title: "Clients", url: "/clients", icon: Users },
              ].map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url || (item.url !== "/dashboard" && location.startsWith(item.url))}
                  >
                    <Link href={item.url} data-testid={`link-${item.title.toLowerCase()}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Quotes — with pending shared badge */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/quotes" || (location.startsWith("/quotes/") && !["new", "template", "manual", "survey"].some((sub) => location.startsWith(`/quotes/${sub}`)))}
                >
                  <Link href="/quotes" data-testid="link-quotes">
                    <FileText className="h-4 w-4" />
                    <span className="flex-1">Quotes</span>
                    {pendingShareCount > 0 && (
                      <span className="ml-auto bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="badge-pending-shares">
                        {pendingShareCount}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location === "/quote-document-design"}>
                  <Link href="/quote-document-design" data-testid="link-quote-document-design">
                    <Palette className="h-4 w-4" />
                    <span className="flex-1">Quote Design</span>
                    <NewFeatureBadge testId="badge-quote-design-new" />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Site Surveys — list of every captured site visit */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/site-surveys" || location.startsWith("/site-surveys/")}
                >
                  <Link href="/site-surveys" data-testid="link-site-surveys">
                    <ClipboardList className="h-4 w-4" />
                    <span>Site Surveys</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-sidebar-foreground">Library</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[
                { title: "Cost Library", url: "/cost-library", icon: BookOpen },
                { title: "Learned Adjustments", url: "/adjustments", icon: TrendingUp },
                { title: "Pricing Intelligence", url: "/pricing-intelligence", icon: BrainCircuit },
              ].map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url || location.startsWith(item.url)}
                  >
                    <Link href={item.url} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-sidebar-foreground">Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[
                { title: "Referrals", url: "/referrals", icon: Gift },
                { title: "Settings", url: "/settings", icon: Settings },
              ].map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url || location.startsWith(item.url + "/")}
                  >
                    <Link href={item.url} data-testid={`link-${item.title.toLowerCase()}`}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdminUser && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sm font-semibold text-sidebar-foreground">Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {[
                  { title: "Users", url: "/admin/users", icon: Users },
                  { title: "Quotes", url: "/admin/quotes", icon: FileText },
                  { title: "Withdrawals", url: "/admin/withdrawals", icon: Landmark },
                  { title: "Partner Referrals", url: "/admin/partner-referrals", icon: Gift },
                  { title: "Account Deletions", url: "/admin/account-deletions", icon: UserX },
                  { title: "Generated Quotes", url: "/admin/generated-quotes", icon: Sparkles },
                  { title: "Vocabulary Gaps", url: "/admin/vocabulary-gaps", icon: SearchCode },
                  { title: "Master Cost Library", url: "/admin/cost-library", icon: Library },
                  { title: "Price Review", url: "/admin/price-review", icon: ClipboardCheck },
                  { title: "Message Centre", url: "/admin/messages", icon: Mail },
                  { title: "Theme Editor", url: "/admin/theme", icon: Palette },
                  { title: "Inbox", url: "/admin/inbox", icon: Inbox },
                  { title: "Maintenance", url: "/admin/maintenance", icon: Wrench },
                ].map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.url || location.startsWith(item.url + "/") || (item.url === "/admin/price-review" && location === "/admin/recipe-drafts") || (item.url === "/admin/messages" && location.startsWith("/admin/updates"))}
                    >
                      <Link href={item.url} data-testid={`link-admin-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {(() => {
        if (!user) return null;
        if (user.subscriptionStatus === "active") return null;
        if (!user.trialEndAt) return null;
        const trialExpired = new Date(user.trialEndAt) <= new Date();
        if (trialExpired) return null;
        const quotaHit = !!(user as any).trialQuotaExceeded || !!(user as any).quotaLimitEnforced;
        if (quotaHit) return null;
        const msLeft = new Date(user.trialEndAt).getTime() - Date.now();
        const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
        const daysLabel = daysLeft === 0
          ? "Free trial (expires today)"
          : `Free trial (${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining)`;
        const isWarning = daysLeft <= 3;
        return (
          <div
            className={`mx-3 mb-2 px-3 py-2 rounded-md text-xs font-bold ${
              isWarning
                ? "text-amber-600 dark:text-amber-400"
                : ""
            }`}
            style={isWarning ? undefined : { color: "#5aabbf" }}
            data-testid="sidebar-trial-badge"
          >
            {daysLabel}
          </div>
        );
      })()}

      <SidebarFooter className="p-4 border-t">
        <div className="flex items-center gap-3">
          <UserAvatar user={user ?? null} className="h-9 w-9" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">
              {user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user?.email || "User"}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.email}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => logout()}
            data-testid="button-logout"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
